import { CommonModule } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
} from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PreloadAllModules, RouterOutlet, provideRouter, withPreloading } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular-ivy';
import { KeycloakAngularModule } from 'keycloak-angular';
import {
  ConfirmationService as PrimengConfirmationService,
  MessageService as PrimengMessageService,
} from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService as PrimengDialogService } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { versions } from '../environments/versions';
import { AppComponent } from './app.component';
import { APP_ROUTES } from './app.routes';
import { DashboardLayoutComponent } from './core/components/layout/dashboard-layout/dashboard-layout.component';
import { appInitializer } from './core/config/app-initializer';
import { registerLocales } from './core/config/register-locales';
import { CustomErrorHandler } from './core/error-handlers/custom-error-handler';
import { RequestInterceptor } from './core/interceptors/http.interceptor';
import { TimezoneInterceptor } from './core/interceptors/timezone.interceptor';
import { AuthService } from './core/services/auth.service';
import { ErrorTrackerService } from './core/services/error-tracker/error-tracker.service';
import { LanguageService } from './core/services/language.service';
import { RouteCheckerService } from './core/services/route-checker.service';
import { UserConfigurationService } from './core/services/user-configuration.service';
import { UserExternalSyncService } from './core/services/user-external-sync.service';
import { ConfirmDialogComponent } from './osp-ui/components/overlay/confirm-dialog/confirm-dialog.component';
import { OspUiModule } from './osp-ui/osp-ui.module';
import { ConfirmationService } from './osp-ui/services/confirmation.service';
import { ImageLoaderProvider } from './osp-ui/services/image-loader-provider.service';
import { MessageService } from './osp-ui/services/message.service';
import { OnlineStatusService } from './osp-ui/services/online-status.service';
import { MaintenanceTaskSearchService } from './pages/maintenance/maintenance-task/consts/maintenance-task-serach-injection-token.const';
import { NotificationsModule } from './pages/notifications/notifications.module';
import { EquipmentSearchService } from './pages/resources/equipment/consts/injection-tokens.const';
import {
  AutomatedTransfersOnTheRoadSearchService,
  AutomatedTransfersScheduledSearchService,
} from './pages/transfers/automated-transfers/consts/injection-tokens.const';
import { MAPS_CONFIG } from './shared/components/maps/consts/maps-config.const';
import { googleMapsConfigFactory } from './shared/components/maps/utils/google-maps-config-factory';
import { RootToastComponent } from './shared/components/root-toast/root-toast.component';
import { FileDownloadService } from './shared/services/file-download.service';
import { SearchService } from './shared/services/search.service';
import { SharedPipesProvidersModule } from './shared/shared-pipes-providers.module';
import { TranslateHttpLoaderFactory, appInitializerTranslationFactory } from './shared/utils/translation-utils';

registerLocales();

Sentry.init({
  dsn: environment.SENTRY,
  maxValueLength: 8192,
  initialScope: {
    tags: { git_hash: versions.commit },
  },
});

@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent],
  imports: [
    KeycloakAngularModule,
    BrowserModule,
    BrowserAnimationsModule,
    FontAwesomeModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: TranslateHttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    CommonModule,
    RouterOutlet,
    TranslateModule,
    NotificationsModule,
    OspUiModule.forRoot([{ provide: ImageLoaderProvider, useExisting: FileDownloadService }]),
    DashboardLayoutComponent,
    RootToastComponent,
    ConfirmDialogComponent,
    ConfirmDialogModule,
    SharedPipesProvidersModule,
    GoogleMapsModule,
  ],
  providers: [
    MessageService,
    ConfirmationService,
    PrimengConfirmationService,
    PrimengMessageService,
    OnlineStatusService,
    PrimengDialogService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TimezoneInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerTranslationFactory,
      deps: [LanguageService, ErrorTrackerService, Injector],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      multi: true,
      deps: [AuthService, UserConfigurationService, LanguageService, UserExternalSyncService, RouteCheckerService],
    },
    {
      provide: MAPS_CONFIG,
      useFactory: googleMapsConfigFactory(environment.GOOGLE_MAPS_API_KEY),
      deps: [LanguageService],
    },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    provideRouter(APP_ROUTES, withPreloading(PreloadAllModules)),
    environment.SENTRY && environment.logErrorsInSentry
      ? { provide: ErrorHandler, useExisting: CustomErrorHandler }
      : [],
    { provide: EquipmentSearchService, useClass: SearchService },
    { provide: AutomatedTransfersOnTheRoadSearchService, useClass: SearchService },
    { provide: AutomatedTransfersScheduledSearchService, useClass: SearchService },
    { provide: MaintenanceTaskSearchService, useClass: SearchService },
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
  ],
})
export class AppModule {}
